/**
 * Me → Natal Chart sub-page.
 *
 * Renders the user's natal disc plus full data tables (planets / houses /
 * angles / aspects / house-occupancy) and the archetype + dominant-planet
 * narrative. Every sign, planet, house and angle is tappable — opens an
 * InfoPopup with a short Western-astrology explainer.
 */

import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../store/auth.js';
import { useNatalReading } from '../../hooks/useNatalReading';
import type { PaidTheme } from '../../hooks/useNatalReading';
import { useSkuInvoice } from '../../hooks/useSkuInvoice';
import { PaymentSheet } from '../../components/payment/PaymentSheet';
import { ReportMarkdown } from '../../components/ReportMarkdown';
import { useTranslation, Trans } from 'react-i18next';
import { MeSubpageShell } from '../../components/chrome/MeSubpageShell';
import { ChartView } from '../../components/me/ChartView';
import { planetLabel } from '../../components/me/chartViewHelpers';
import { useChart } from '../../hooks/useChart';
import styles from './MeSubpages.module.css';
import natal from './NatalChart.module.css';

export function NatalChart(): JSX.Element {
  const { t, i18n } = useTranslation(['me', 'common']);
  const { data, isLoading, isError } = useChart();
  const [readingEnabled, setReadingEnabled] = useState(false);
  const [readingExpanded, setReadingExpanded] = useState(true);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const userId = useAuth((s) => s.user?.id);

  // Deep-link: natal_reading token → auto-open reading without button tap
  useEffect(() => {
    if (searchParams.get('autoLoad') === '1') {
      setReadingEnabled(true);
    }
  // Only run on mount (searchParams is stable for the initial URL)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const { data: readingData, isLoading: readingLoading, isError: readingError, refetch: readingRefetch } = useNatalReading(readingEnabled);

  // Paid theme state
  const [activeTheme, setActiveTheme] = useState<PaidTheme | null>(null);
  const [payingTheme, setPayingTheme] = useState<PaidTheme | null>(null);
  const [pollThemes, setPollThemes] = useState<Set<PaidTheme>>(new Set());
  const { purchase, redeemCoupon } = useSkuInvoice<{ theme: PaidTheme; src: 'miniapp' }>();

  const chartReady = data?.status === 'ready';
  // Enable each paid-theme query ON MOUNT (cheap GET → 200 owned / 402 not) so a
  // returning buyer sees their reading immediately (and is never re-charged), and
  // so we know whether to show the bundle. The 3rd arg keeps polling AFTER a
  // purchase until the settle handler has written the row.
  const loveReading = useNatalReading(chartReady, 'love', pollThemes.has('love'));
  const careerReading = useNatalReading(chartReady, 'career', pollThemes.has('career'));
  const purposeReading = useNatalReading(chartReady, 'purpose', pollThemes.has('purpose'));

  const themeReadings: Record<PaidTheme, typeof loveReading> = {
    love: loveReading,
    career: careerReading,
    purpose: purposeReading,
  };

  const THEME_DEFS: Array<{ key: PaidTheme }> = [
    { key: 'love' },
    { key: 'career' },
    { key: 'purpose' },
  ];

  // Bundle (59⭐ for all 3) is only the better deal BEFORE you own any theme: once
  // you own ≥1, the remaining cost 25⭐ each (< 59) and the bundle would re-charge
  // for the owned one. So hide the bundle after the first purchase.
  const ownedThemeCount = THEME_DEFS.filter(({ key }) => themeReadings[key].data?.ready === true).length;

  const handlePayTheme = async (theme: PaidTheme): Promise<void> => {
    setPayingTheme(theme);
    try {
      const result = await purchase('natal_theme', { theme, src: 'miniapp' });
      if (result.status === 'paid') {
        setActiveTheme(null);
        setPollThemes((prev) => new Set([...prev, theme]));
      }
    } catch {
      // error state handled by invoiceState
    } finally {
      setPayingTheme(null);
    }
  };

  const handlePayBundle = async (): Promise<void> => {
    setPayingTheme('love');
    try {
      const result = await purchase('natal_bundle', { src: 'miniapp' } as unknown as { theme: PaidTheme; src: 'miniapp' });
      if (result.status === 'paid') {
        setActiveTheme(null);
        setPollThemes(new Set(['love', 'career', 'purpose'] as PaidTheme[]));
      }
    } catch {
      // error state handled by invoiceState
    } finally {
      setPayingTheme(null);
    }
  };

  return (
    <MeSubpageShell title={t('natalChart.chrome.pageTitle')} data-testid="natal-chart-page">
      {isLoading && (
        <p className={styles.cardMeta} data-testid="natal-chart-loading">{t('common:states.loading')}</p>
      )}
      {isError && (
        <p role="alert" data-testid="natal-chart-error">{t('natalChart.chrome.loadError')}</p>
      )}
      {!isLoading && !isError && data?.status === 'pending' && (
        <p data-testid="natal-chart-pending" className={styles.cardMeta}>
          {t('natalChart.chrome.pending')}
        </p>
      )}
      {!isLoading && !isError && data?.status === 'ready' && (
        <div data-testid="natal-chart-ready">
          <ChartView
            data={{
              planets: data.planets ?? [],
              houses: data.houses ?? undefined,
              angles: data.angles ?? undefined,
              aspects: data.aspects ?? undefined,
            }}
            header={(
              <>
                {data.archetype && (
                  <>
                    <div className={styles.archetypeKicker}>{t('natalChart.chrome.archetypeKicker')}</div>
                    <div className={styles.archetypeName} data-testid="natal-archetype">
                      {data.archetype.label}
                    </div>
                  </>
                )}
                {data.dominant && (
                  <div className={styles.dominantNote} data-testid="natal-dominant">
                    {t('natalChart.chrome.dominant', { planet: planetLabel(data.dominant.planet, t) })}
                  </div>
                )}
                <button
                  type="button"
                  className={natal.discussButton}
                  onClick={() => navigate(`/chat?surface=natal&seedRef=natal:${userId ?? ''}`)}
                  data-testid="natal-discuss"
                >
                  {t('natalChart.chrome.discuss')}
                </button>
              </>
            )}
          >
          {/* ── Natal Portrait button + reading — ABOVE PlanetsTable ── */}
          <div className={natal.readingSection}>
            {!readingEnabled && !readingData && (
              <button
                type="button"
                data-testid="natal-reading-button"
                className={natal.readingButton}
                onClick={() => {
                  setReadingEnabled(true);
                  setReadingExpanded(true);
                }}
              >
                {t('natalReading.buttonLabel')}
              </button>
            )}

            {readingLoading && (
              <p
                data-testid="natal-reading-loading"
                className={natal.readingLoading}
              >
                {t('natalReading.loadingLabel')}
              </p>
            )}

            {(readingError || (readingData && !readingData.ready)) && (
              <button
                type="button"
                data-testid="natal-reading-error"
                className={natal.readingError}
                onClick={() => readingRefetch()}
              >
                {t('natalReading.errorLabel')}
              </button>
            )}

            {readingData?.ready && (
              <div data-testid="natal-reading-card" className={natal.readingCard}>
                <div className={natal.readingHead}>
                  <h2 className={natal.readingTitle}>
                    {t('natalReading.sectionTitle')}
                  </h2>
                  <button
                    type="button"
                    className={natal.readingToggle}
                    onClick={() => setReadingExpanded((v) => !v)}
                  >
                    {readingExpanded ? t('natalReading.collapseLabel') : t('natalReading.expandLabel')}
                  </button>
                </div>

                {readingExpanded && (
                  <>
                    <ReportMarkdown markdown={readingData.markdown} />
                    {/* EU AI Act Art. 50 compliance label */}
                    <p
                      data-testid="natal-reading-ai-label"
                      className={natal.aiLabel}
                    >
                      {t('natalReading.aiLabel')}
                    </p>
                    {/* Entertainment disclaimer */}
                    <p
                      data-testid="natal-reading-disclaimer"
                      className={natal.disclaimer}
                    >
                      {t('natalReading.disclaimer')}
                    </p>
                  </>
                )}
              </div>
            )}

            {/* ── Paid theme deep-dives section ── */}
            <div className={natal.deepDives}>
              <h3 className={natal.deepDivesTitle}>
                {t('natalReading.deepDivesSection')}
              </h3>
              <p className={natal.deepDivesSubtitle}>
                {t('natalReading.deepDivesSubtitle')}
              </p>

              {THEME_DEFS.map(({ key }) => {
                const reading = themeReadings[key];
                const isOwned = reading.data?.ready === true;
                const isPollPending = pollThemes.has(key) && !isOwned;

                return (
                  <div
                    key={key}
                    data-testid={`natal-theme-card-${key}`}
                    className={`${natal.themeCard} ${isOwned ? natal.themeCardOwned : ''}`.trim()}
                  >
                    <div className={natal.themeRow}>
                      <div className={natal.themeText}>
                        <div className={natal.themeTitle}>
                          {t(`natalReading.themes.${key}.title`)}
                        </div>
                        <div className={natal.themeTeaser}>
                          {t(`natalReading.themes.${key}.teaser`)}
                        </div>
                      </div>
                      <div className={natal.themeAction}>
                        {isOwned ? (
                          <button
                            type="button"
                            data-testid={`natal-theme-read-${key}`}
                            className={natal.themeRead}
                            onClick={() => setPollThemes((prev) => new Set([...prev, key]))}
                          >
                            {t(`natalReading.themes.${key}.readLabel`)}
                          </button>
                        ) : isPollPending ? (
                          <span className={natal.themePending}>
                            {t('natalReading.paidLoadingLabel')}
                          </span>
                        ) : (
                          <button
                            type="button"
                            data-testid={`natal-theme-buy-${key}`}
                            className={natal.themeBuy}
                            onClick={() => setActiveTheme(key)}
                          >
                            25 ⭐
                          </button>
                        )}
                      </div>
                    </div>

                    {isOwned && reading.data?.ready && (
                      <div data-testid={`natal-theme-reading-${key}`} className={natal.themeReading}>
                        <ReportMarkdown markdown={reading.data.markdown} />
                        <p className={natal.themeAiLabel}>
                          {t('natalReading.aiLabel')}
                        </p>
                        <p className={natal.themeDisclaimer}>
                          {t('natalReading.disclaimer')}
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Bundle card — hidden once the user owns ≥1 theme (no longer the better deal) */}
              {ownedThemeCount === 0 && (
              <div
                data-testid="natal-bundle-card"
                className={natal.bundleCard}
              >
                <div className={natal.bundleTitle}>
                  {t('natalReading.bundle.title')}
                </div>
                <div className={natal.bundleSubtitle}>
                  {t('natalReading.bundle.subtitle')}
                </div>
                <button
                  type="button"
                  data-testid="natal-bundle-buy"
                  className={natal.bundleBuy}
                  onClick={() => void handlePayBundle()}
                  disabled={payingTheme !== null}
                >
                  {t('natalReading.bundle.cta')}
                </button>
              </div>
              )}
            </div>
          </div>

          </ChartView>

          {data.archetype && (
            <p className={styles.bodyText} data-testid="natal-archetype-body">
              {data.dominant ? (
                <Trans
                  t={t}
                  i18nKey="natalChart.chrome.archetypeBodyWithDominant"
                  values={{ archetype: data.archetype.label, dominant: planetLabel(data.dominant.planet, t) }}
                  components={[<strong key="a" />, <strong key="d" />]}
                />
              ) : (
                <Trans
                  t={t}
                  i18nKey="natalChart.chrome.archetypeBody"
                  values={{ archetype: data.archetype.label }}
                  components={[<strong key="a" />]}
                />
              )}
            </p>
          )}

          <p className={styles.metaFoot}>
            {t('natalChart.chrome.computed', {
              date: new Date(data.computedAt).toLocaleDateString(
                i18n.language === 'en' ? 'en-GB' : (i18n.language || 'en-GB'),
                { day: 'numeric', month: 'short', year: 'numeric' },
              ),
            })}
          </p>
        </div>
      )}

      {activeTheme && (
        <PaymentSheet
          skuCode="natal_theme"
          title={t(`natalReading.themes.${activeTheme}.title`)}
          subtitle={t(`natalReading.themes.${activeTheme}.teaser`)}
          priceStars={25}
          bullets={[]}
          isPaying={payingTheme === activeTheme}
          onPay={() => void handlePayTheme(activeTheme)}
          onApplyCoupon={async (code) => {
            setPayingTheme(activeTheme);
            try {
              const result = await redeemCoupon('natal_theme', code, { theme: activeTheme, src: 'miniapp' });
              if (result.status === 'paid') {
                setActiveTheme(null);
                setPollThemes((prev) => new Set([...prev, activeTheme]));
              }
            } finally {
              setPayingTheme(null);
            }
          }}
          onCancel={() => setActiveTheme(null)}
          testId="natal-theme-payment-sheet"
        />
      )}
    </MeSubpageShell>
  );
}
